import { Box, Grid } from "@mui/material";
import {
  Assignment,
  ChecklistRtl,
  Inventory2,
} from "@mui/icons-material";
import CriticalStockTable from "./components/CriticalStockTable.js";
import MonthlyChart from "./components/MonthlyChart.js";
import StatsCards from "./components/StatsCards.js";
import QuickActions from "./components/QuickActions.js";
import StockChart from "./components/StockChart.js";
import { React, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dashboardService from "../../services/dashboradService.js";
import AuthService from "../../services/authService.js";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const result = await dashboardService.getDashboard();
      if (result?.data) {
        setDashboardData(result.data);
      } else {
        setDashboardData([]);
      }
      setError(null);
    } catch (error) {
      console.error("Error al obtener datos:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // Solo mostramos cada acceso rápido si el usuario puede crear ese recurso, igual
  // que el sidebar y coherente con el enforcement del backend (crear ingreso/egreso/
  // producto devuelve 403 sin el permiso). El owner ve todo.
  const canCreate = (tab) =>
    AuthService.isOwner() || AuthService.hasPermission("create", tab);

  const quickActions = [
    canCreate("movement_out") && {
      title: "Nuevo Egreso",
      icon: <Assignment />,
      path: "/create-egress",
      color: "#3b82f6",
    },
    canCreate("movement_in") && {
      title: "Nuevo Ingreso",
      icon: <ChecklistRtl />,
      path: "/create-ingress",
      color: "#10b981",
    },
    canCreate("product") && {
      title: "Nuevo Producto",
      icon: <Inventory2 />,
      path: "/create-product",
      color: "#8b5cf6",
    },
  ].filter(Boolean);

  const MONTH_LABELS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  const currentYear = new Date().getFullYear();

  // Movimientos por mes (dato real del backend: monthly_trend).
  const monthlyData = (dashboardData?.monthly_trend || []).map((m) => ({
    month: MONTH_LABELS[(m.month || 1) - 1] || "",
    cantidad: m.count || 0,
    promedio: m.average || 0,
  }));

  // Stock crítico: top_products real, ordenado por cercanía al quiebre
  // (los que tienen días calculados primero; 0 = sin salida/sin dato, al final).
  const criticalStockData = (dashboardData?.top_products || [])
    .map((p) => ({
      sku: p.sku,
      pendienteIngreso: p.pending_in || 0,
      stock: p.stock || 0,
      pendienteEgreso: p.pending_out || 0,
      ranking: p.ranking || 0,
      semanas: p.days_to_stock_break || 0,
    }))
    .sort((a, b) => {
      const da = a.semanas > 0 ? a.semanas : Infinity;
      const db = b.semanas > 0 ? b.semanas : Infinity;
      return da - db;
    })
    .slice(0, 6);

  const getSemanasColor = (dias) => {
    if (dias <= 3.5) return "#ef4444";
    if (dias <= 4.5) return "#f59e0b";
    return "#10b981";
  };

  const totalProducts =
    (dashboardData?.in_stock_products || 0) +
    (dashboardData?.out_of_stock_products || 0);

  const stockData = [
    {
      name: "En stock",
      value: dashboardData?.in_stock_products || 0,
      percentage: totalProducts > 0
        ? parseFloat((((dashboardData?.in_stock_products || 0) / totalProducts) * 100).toFixed(1))
        : 0,
    },
    {
      name: "Sin stock",
      value: dashboardData?.out_of_stock_products || 0,
      percentage: totalProducts > 0
        ? parseFloat((((dashboardData?.out_of_stock_products || 0) / totalProducts) * 100).toFixed(1))
        : 0,
    },
  ];

  return (
    <Box
      sx={{
        padding: { xs: "16px", md: "28px 32px" },
        maxWidth: "1400px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "28px",
      }}
    >
      {/* Row 1: KPI Stats Cards */}
      <StatsCards data={dashboardData} loading={loading} />

      {/* Row 2: Quick Actions + Stock Chart. Si el usuario no puede crear nada,
          ocultamos la card de acciones y el gráfico ocupa todo el ancho. */}
      <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
        {quickActions.length > 0 && (
          <Grid item xs={12} md={4} sx={{ display: "flex" }}>
            <QuickActions actions={quickActions} />
          </Grid>
        )}
        <Grid item xs={12} md={quickActions.length > 0 ? 8 : 12} sx={{ display: "flex" }}>
          <StockChart stockData={stockData} totalProducts={totalProducts} />
        </Grid>
      </Grid>

      {/* Row 3: Monthly Chart + Critical Stock Table */}
      <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
        <Grid item xs={12} md={7} sx={{ display: "flex" }}>
          <MonthlyChart data={monthlyData} year={currentYear} />
        </Grid>
        <Grid item xs={12} md={5} sx={{ display: "flex" }}>
          <CriticalStockTable
            data={criticalStockData}
            getSemanasColor={getSemanasColor}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
